import { useCallback, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { stringToNumber } from "../utils/strings";
import {
  searchParamsToObject,
  objectToSearchParams,
} from "../utils/queryString";
import { formatTagsForServer } from "../components/SearchListings/TagSearch";

interface FilterConfig {
  name: string;
  apiKeyName?: string;
  excludeFromSearchQuery?: boolean;
  prepareApiValue?: (value: unknown) => unknown;
}

interface UseSearchCommonParams {
  defaultPageSize?: number;
  defaultSortField?: string;
  defaultSortDir?: string;
  customFilters?: Record<string, unknown>;
  filterConfigs?: FilterConfig[];
}

interface UseSearchCommonResult {
  currentPage: number;
  pageSize: number;
  sortField: string;
  sortDir: string;
  searchParams: URLSearchParams;
  customFilters: Record<string, unknown>;
  searchApiQueryParams: URLSearchParams;
  updateSearchParams: (updates: Record<string, unknown>) => void;
  onSearchParamsUpdated: (updates: Record<string, unknown>) => void;
  resetFilters: (resetConfig?: Array<{ name: string; defaultValue: unknown; exclude?: boolean }>) => void;
  scrollToTop: () => void;
}

export const useSearchCommon = ({
  defaultPageSize = 25,
  defaultSortField = "date",
  defaultSortDir = "desc",
  customFilters = {},
  filterConfigs = [],
}: UseSearchCommonParams = {}): UseSearchCommonResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef(false);

  // Extract basic search parameters from URL
  const currentPage = stringToNumber(searchParams.get("currentPage") ?? "", 1);
  const pageSize = stringToNumber(searchParams.get("pageSize") ?? "", defaultPageSize);
  const sortDir = searchParams.get("sortDir") || defaultSortDir;
  const sortField = searchParams.get("sortField") || defaultSortField;

  // Extract custom filter values from URL parameters
  const extractedCustomFilters = useMemo(() => {
    const filters = Object.keys(customFilters).reduce((acc, key) => {
      // Handle array values using getAll()
      if (Array.isArray(customFilters[key])) {
        const urlValues = searchParams.getAll(key);
        acc[key] = urlValues.length > 0 ? urlValues : customFilters[key];
      } else {
        const urlValue = searchParams.get(key);
        if (urlValue !== null) {
          // Convert string boolean values back to actual booleans
          if (typeof customFilters[key] === 'boolean') {
            acc[key] = urlValue === 'true';
          } else {
            acc[key] = urlValue;
          }
        } else {
          acc[key] = customFilters[key];
        }
      }
      return acc;
    }, {} as Record<string, unknown>);
    
    return filters;
  }, [searchParams, customFilters]);

  // Generate API query parameters based on filter configurations
  const searchApiQueryParams = useMemo(() => {
    const allValues: Record<string, unknown> = {
      currentPage,
      pageSize,
      sortField,
      sortDir,
      ...extractedCustomFilters,
    };

    const apiParams = filterConfigs.reduce((acc, config) => {
      const { name, apiKeyName, excludeFromSearchQuery, prepareApiValue } = config;
      const value = allValues[name];

      if (excludeFromSearchQuery || value === "" || value === undefined) {
        return acc;
      }

      const apiKey = apiKeyName || name;
      
      // Special handling for tags formatting
      if (name === 'tags' && typeof value === 'string') {
        const isTagsAndSearch = allValues.isTagsAndSearch as boolean;
        acc[apiKey] = formatTagsForServer(value, isTagsAndSearch || false);
      } else {
        acc[apiKey] = prepareApiValue ? prepareApiValue(value) : value;
      }
      
      return acc;
    }, {} as Record<string, unknown>);

    const queryParams = objectToSearchParams(apiParams);
    queryParams.set("offset", String((currentPage - 1) * pageSize));
    return queryParams;
  }, [currentPage, pageSize, sortField, sortDir, extractedCustomFilters, filterConfigs]);

  // Update search parameters in URL
  const updateSearchParams = useCallback(
    (updatedParams: Record<string, unknown>) => {
      const currentParams = searchParamsToObject(searchParams);
      const newParams = {
        ...currentParams,
        ...updatedParams,
      }
      const updateURLSearchParams = objectToSearchParams(newParams);
      setSearchParams(updateURLSearchParams);
    },
    [searchParams, setSearchParams]
  );

  // Scroll to top of page
  const scrollToTop = useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle search parameters update with scroll to top
  const handleSearchParamsUpdated = useCallback(
    (updates: Record<string, unknown>) => {
      updateSearchParams(updates);
      scrollToTop();
    },
    [updateSearchParams, scrollToTop]
  );


  // Reset filters to default values
  const resetFilters = useCallback(
    (resetConfig?: Array<{ name: string; defaultValue: unknown; exclude?: boolean }>) => {
      if (resetConfig) {
        // Use provided reset configuration
        const resetParams = resetConfig
          .filter(({ exclude }) => !exclude)
          .reduce((acc, { name, defaultValue }) => {
            acc[name] = defaultValue !== undefined ? defaultValue : "";
            return acc;
          }, {} as Record<string, unknown>);
        
        updateSearchParams(resetParams);
      } else {
        // Reset to default customFilters if no config provided
        const resetParams = {
          currentPage: 1,
          pageSize: defaultPageSize,
          sortField: defaultSortField,
          sortDir: defaultSortDir,
          ...customFilters,
        };
        
        updateSearchParams(resetParams);
      }
    },
    [updateSearchParams, defaultPageSize, defaultSortField, defaultSortDir, customFilters]
  );

  // Initialize URL parameters with default values on first load
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      
      // Check if URL is empty (no search parameters)
      const hasUrlParams = searchParams.toString().length > 0;
      
      if (!hasUrlParams) {
        // Initialize URL with non-empty default values
        const defaultParams: Record<string, unknown> = {};
        
        // Add page parameters that should always be visible
        defaultParams.currentPage = 1;
        defaultParams.pageSize = defaultPageSize;
        defaultParams.sortField = defaultSortField;
        defaultParams.sortDir = defaultSortDir;
        
        // Add non-empty custom filter defaults
        Object.entries(customFilters).forEach(([key, value]) => {
          // Only add meaningful defaults (not empty strings, false, empty arrays)
          if (value !== "" && value !== false && !(Array.isArray(value) && value.length === 0)) {
            defaultParams[key] = value;
          }
        });
        
        // Update URL with defaults
        setSearchParams(objectToSearchParams(defaultParams));
      }
    }
  }, [searchParams, setSearchParams, defaultPageSize, defaultSortField, defaultSortDir, customFilters]);

  return {
    currentPage,
    pageSize,
    sortField,
    sortDir,
    searchParams,
    customFilters: extractedCustomFilters,
    searchApiQueryParams,
    updateSearchParams,
    onSearchParamsUpdated: handleSearchParamsUpdated,
    resetFilters,
    scrollToTop,
  };
}; 